/**
 * Leave & Holidays Helper Functions
 *
 * Provides robust null-handling and formatting for leave-related fields.
 * Ensures FE sends exactly what BE expects (Decimal strings, null for empty).
 */

/**
 * Converts a value to a decimal string with 2 decimal places, or null if empty.
 * Empty field => null (triggers auto-calculation on backend)
 * Non-empty => "X.XX" (2 decimal places as string)
 */
export function toNullableDecimalString(v: string | number | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  const trimmed = String(v).trim();
  if (trimmed === '') return null;  // Empty field => null (Auto-calculation)

  const n = Number(trimmed.replace(',', '.'));
  if (Number.isNaN(n)) return null;  // Defensive: invalid number => null

  return n.toFixed(2);  // 2 decimal places as string
}

/**
 * Converts a value to a year (integer), or null if empty.
 * Empty field => null
 * Valid year (2000-2100) => number
 */
export function toYearOrNull(v: string | number | null | undefined): number | null {
  const trimmed = (v ?? '').toString().trim();
  if (trimmed === '') return null;

  const y = Number(trimmed);
  if (Number.isNaN(y)) return null;

  // FE guard - BE will validate anyway
  return (y >= 2000 && y <= 2100) ? y : null;
}

/**
 * Parses a decimal string back to a number for display in input fields.
 * Returns empty string if null/undefined (for controlled inputs).
 */
export function parseDecimalString(v: string | null | undefined): string {
  if (!v) return '';
  const n = parseFloat(v);
  return Number.isFinite(n) ? String(n) : '';
}
